import type { ModelDefinition, SyncRecord, Transaction } from "./types";

type Listener = () => void;
export type StoredRecord = { model: string; id: string; data: SyncRecord; syncId: number };

const keyOf = (model: string, id: string) => `${model}\u0000${id}`;

function getOrCreate<K, V>(map: Map<K, V>, key: K, create: () => V): V {
	let value = map.get(key);
	if (value === undefined) {
		value = create();
		map.set(key, value);
	}
	return value;
}

function shallowEqual(a: SyncRecord | undefined, b: SyncRecord | undefined) {
	if (a === b) return true;
	if (!a || !b) return false;
	const keys = Object.keys(a);
	if (keys.length !== Object.keys(b).length) return false;
	return keys.every((key) => a[key] === b[key]);
}

/**
 * In-memory object graph the UI reads from. Visible state = confirmed server state with
 * pending (optimistic) transactions replayed on top, so rollbacks are just "drop the tx".
 * Subscriptions are per record and per model, and notifications are batched per microtask.
 */
export class ObjectPool {
	private confirmed = new Map<string, Map<string, SyncRecord>>();
	private syncIds = new Map<string, number>();
	private visible = new Map<string, Map<string, SyncRecord>>();
	private pending: Transaction[] = [];
	private indexes = new Map<string, Map<string, Map<unknown, Set<string>>>>();
	private lists = new Map<string, SyncRecord[]>();
	private recordListeners = new Map<string, Set<Listener>>();
	private modelListeners = new Map<string, Set<Listener>>();
	private dirtyRecords = new Set<string>();
	private dirtyModels = new Set<string>();
	private notifyScheduled = false;

	constructor(private readonly definitions: Map<string, ModelDefinition>) {}

	get(model: string, id: string): SyncRecord | undefined {
		return this.visible.get(model)?.get(id);
	}

	/** Stable array per model version — safe as a `useSyncExternalStore` snapshot. */
	list(model: string): SyncRecord[] {
		let list = this.lists.get(model);
		if (!list) {
			list = Array.from(this.visible.get(model)?.values() ?? []);
			const sort = this.definitions.get(model)?.sort as
				| ((a: SyncRecord, b: SyncRecord) => number)
				| undefined;
			if (sort) list.sort(sort);
			this.lists.set(model, list);
		}
		return list;
	}

	/** Ids matching `field === value`, using the equality index when the model declares one. */
	idsWhere(model: string, field: string, value: unknown): string[] | null {
		const index = this.indexes.get(model)?.get(field);
		if (!index) return null;
		return Array.from(index.get(value) ?? []);
	}

	pendingTransactions(): readonly Transaction[] {
		return this.pending;
	}

	hasPending(model: string, id: string) {
		return this.pending.some((tx) => tx.model === model && tx.modelId === id);
	}

	hydrate(records: StoredRecord[], pending: Transaction[]) {
		const touched = new Set<string>();
		for (const { model, id, data, syncId } of records) {
			this.confirmedMap(model).set(id, data);
			this.syncIds.set(keyOf(model, id), syncId);
			touched.add(keyOf(model, id));
		}
		this.pending = [...pending].sort((a, b) => a.createdAt - b.createdAt);
		for (const tx of this.pending) touched.add(keyOf(tx.model, tx.modelId));
		for (const key of touched) {
			const [model, id] = key.split("\u0000");
			this.recompute(model, id);
		}
	}

	/** Applies an authoritative server row. Ignores changes older than what we already hold. */
	applyServer(model: string, record: SyncRecord, syncId: number | null): boolean {
		const key = keyOf(model, record.id);
		const known = this.syncIds.get(key) ?? -1;
		if (syncId !== null && syncId < known) return false;
		this.confirmedMap(model).set(record.id, record);
		this.syncIds.set(key, syncId ?? known);
		this.recompute(model, record.id);
		return true;
	}

	removeServer(model: string, id: string, syncId: number | null): boolean {
		const key = keyOf(model, id);
		if (syncId !== null && syncId < (this.syncIds.get(key) ?? -1)) return false;
		this.confirmed.get(model)?.delete(id);
		this.syncIds.delete(key);
		this.recompute(model, id);
		return true;
	}

	/** Last server-confirmed version of a record (what gets persisted), without pending txs. */
	getConfirmed(model: string, id: string): StoredRecord | undefined {
		const data = this.confirmed.get(model)?.get(id);
		return data && { model, id, data, syncId: this.syncIds.get(keyOf(model, id)) ?? 0 };
	}

	addPending(tx: Transaction) {
		this.pending.push(tx);
		this.recompute(tx.model, tx.modelId);
	}

	/** Server accepted the tx: drop it from the replay log and adopt the server row. */
	resolvePending(tx: Transaction, record: SyncRecord | null, syncId: number | null) {
		this.pending = this.pending.filter((t) => t.id !== tx.id);
		if (record) this.applyServer(tx.model, record, syncId);
		else if (tx.op === "delete") this.removeServer(tx.model, tx.modelId, syncId);
		else this.recompute(tx.model, tx.modelId);
	}

	/** Server rejected the tx: dropping it reverts the record to confirmed + remaining txs. */
	rejectPending(tx: Transaction) {
		this.pending = this.pending.filter((t) => t.id !== tx.id);
		this.recompute(tx.model, tx.modelId);
	}

	clear() {
		const models = new Set([...this.visible.keys(), ...this.confirmed.keys()]);
		const ids = [...this.visible.entries()].flatMap(([model, map]) =>
			[...map.keys()].map((id) => keyOf(model, id)),
		);
		this.confirmed.clear();
		this.syncIds.clear();
		this.visible.clear();
		this.indexes.clear();
		this.lists.clear();
		this.pending = [];
		for (const model of models) this.dirtyModels.add(model);
		for (const key of ids) this.dirtyRecords.add(key);
		this.scheduleNotify();
	}

	subscribeRecord(model: string, id: string, listener: Listener) {
		return this.subscribe(this.recordListeners, keyOf(model, id), listener);
	}

	subscribeModel(model: string, listener: Listener) {
		return this.subscribe(this.modelListeners, model, listener);
	}

	private subscribe(map: Map<string, Set<Listener>>, key: string, listener: Listener) {
		const set = getOrCreate(map, key, () => new Set<Listener>());
		set.add(listener);
		return () => {
			set.delete(listener);
			if (!set.size) map.delete(key);
		};
	}

	private confirmedMap(model: string) {
		return getOrCreate(this.confirmed, model, () => new Map<string, SyncRecord>());
	}

	private recompute(model: string, id: string) {
		let next: SyncRecord | undefined = this.confirmed.get(model)?.get(id);
		for (const tx of this.pending) {
			if (tx.model !== model || tx.modelId !== id) continue;
			if (tx.op === "create") next = next ?? tx.optimistic ?? ({ id, ...tx.data } as SyncRecord);
			else if (tx.op === "update") next = next ? { ...next, ...tx.data } : next;
			else next = undefined;
		}

		const visible = getOrCreate(this.visible, model, () => new Map<string, SyncRecord>());
		const prev = visible.get(id);
		if (shallowEqual(prev, next)) return;

		if (next) visible.set(id, next);
		else visible.delete(id);
		this.reindex(model, id, prev, next);
		this.lists.delete(model);
		this.dirtyRecords.add(keyOf(model, id));
		this.dirtyModels.add(model);
		this.scheduleNotify();
	}

	private reindex(model: string, id: string, prev?: SyncRecord, next?: SyncRecord) {
		const fields = this.definitions.get(model)?.indexes as string[] | undefined;
		if (!fields?.length) return;
		const byField = getOrCreate(
			this.indexes,
			model,
			() => new Map<string, Map<unknown, Set<string>>>(),
		);
		for (const field of fields) {
			const index = getOrCreate(byField, field, () => new Map<unknown, Set<string>>());
			if (prev) index.get(prev[field])?.delete(id);
			if (next) getOrCreate(index, next[field], () => new Set<string>()).add(id);
		}
	}

	private scheduleNotify() {
		if (this.notifyScheduled) return;
		this.notifyScheduled = true;
		queueMicrotask(() => {
			this.notifyScheduled = false;
			const records = [...this.dirtyRecords];
			const models = [...this.dirtyModels];
			this.dirtyRecords.clear();
			this.dirtyModels.clear();
			for (const key of records)
				for (const listener of this.recordListeners.get(key) ?? []) listener();
			for (const model of models)
				for (const listener of this.modelListeners.get(model) ?? []) listener();
		});
	}
}
